import React, { useState } from "react";
import { format, parseISO } from "date-fns";
import { it as itLocale, enUS as enLocale } from "date-fns/locale";
import { CheckCircle, Circle, Plus, Trash, PencilSimple, Check, X, CalendarBlank } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import DateField from "@/components/DateField";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";

// Rendered inside the expanded "Approfondisci" area of a checklist item.
// Handles create / toggle / edit-note / edit-deadline / delete of subtasks.
export default function SubtasksPanel({
  t,
  parentItem,
  templateId,
  testidPrefix,
  onRefresh,
  isAnonymous,
  onAnonymousMutation,
}) {
  const { lang } = useApp();
  const locale = lang === "en" ? enLocale : itLocale;

  const subtasks = parentItem.subtasks || [];
  const [draftTitle, setDraftTitle] = useState("");
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null); // for user subtask title edit
  const [editDraft, setEditDraft] = useState("");

  const sorted = [...subtasks].sort((a, b) => {
    const ta = a.deadline ? Date.parse(a.deadline) : null;
    const tb = b.deadline ? Date.parse(b.deadline) : null;
    if (ta == null && tb == null) return 0;
    if (ta == null) return 1;
    if (tb == null) return -1;
    return ta - tb;
  });

  const upsert = async (payload) => {
    if (isAnonymous) { onAnonymousMutation?.(t.common.authReasonAddCustom); return; }
    await api.post("/checklists/subtask/upsert", {
      template_id: templateId,
      item_id: parentItem.id,
      ...payload,
    });
    await onRefresh();
  };

  const removeSubtask = async (subtaskId) => {
    if (isAnonymous) { onAnonymousMutation?.(t.common.authReasonDeleteCustom); return; }
    // eslint-disable-next-line no-alert
    if (!window.confirm(t.modes.organizzativa.customFormDeleteConfirm)) return;
    await api.delete(`/checklists/subtask/${templateId}/${parentItem.id}/${subtaskId}`);
    await onRefresh();
  };

  // Guards the "start editing subtask title" action so anonymous users get
  // the popup up-front instead of entering the edit UI and only being
  // blocked at save time.
  const startEditingSubtask = (subId, currentTitle) => {
    if (isAnonymous) { onAnonymousMutation?.(t.common.authReasonEditCustom); return; }
    setEditingId(subId);
    setEditDraft(currentTitle || "");
  };

  const addNew = async () => {
    const title = draftTitle.trim();
    if (!title) return;
    setCreating(true);
    try {
      await upsert({ title });
      setDraftTitle("");
    } finally {
      setCreating(false);
    }
  };

  const onKeyAdd = (e) => {
    if (e.key === "Enter") { e.preventDefault(); addNew(); }
  };

  return (
    <div data-testid={`${testidPrefix}-subtasks-${parentItem.id}`} className="pt-4 space-y-3">
      <div className="text-[11px] uppercase tracking-widest font-sans font-bold text-ink-soft">
        {t.modes.organizzativa.subtasksTitle}
      </div>

      {sorted.length > 0 && (
        <ul className="space-y-2">
          {sorted.map(sub => {
            const isSystem = sub.system;
            const dueLabel = sub.deadline ? format(parseISO(sub.deadline), "d MMM yyyy", { locale }) : null;
            const isEditing = editingId === sub.id;
            return (
              <li
                key={sub.id}
                data-testid={`${testidPrefix}-subtask-${sub.id}`}
                data-system={isSystem ? "true" : "false"}
                className="rounded-xl bg-surface/60 border border-border/60 p-3 space-y-2"
              >
                <div className="flex items-start gap-2">
                  <button
                    type="button"
                    onClick={() => upsert({ subtask_id: sub.id, checked: !sub.checked, title: isSystem ? undefined : sub.title })}
                    data-testid={`${testidPrefix}-subtask-toggle-${sub.id}`}
                    className="mt-0.5 shrink-0"
                  >
                    {sub.checked
                      ? <CheckCircle size={20} weight="duotone" className="text-sage" />
                      : <Circle size={20} weight="regular" className="text-ink-soft/50" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    {isEditing ? (
                      <div className="flex items-center gap-1">
                        <Input
                          data-testid={`${testidPrefix}-subtask-title-input-${sub.id}`}
                          value={editDraft}
                          onChange={(e) => setEditDraft(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") { upsert({ subtask_id: sub.id, title: editDraft }).then(() => setEditingId(null)); } }}
                          className="h-8 text-sm rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => upsert({ subtask_id: sub.id, title: editDraft }).then(() => setEditingId(null))}
                          className="text-terracotta"
                          aria-label={t.modes.organizzativa.subtaskSave}
                          data-testid={`${testidPrefix}-subtask-title-save-${sub.id}`}
                        >
                          <Check size={16} weight="bold" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditingId(null)}
                          className="text-ink-soft"
                          aria-label={t.modes.organizzativa.subtaskCancel}
                        >
                          <X size={16} weight="bold" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-start gap-2 flex-wrap">
                        <span className={`font-sans font-medium text-ink ${sub.checked ? "opacity-60" : ""}`}>
                          {sub.title}
                        </span>
                        {isSystem && (
                          <span className="text-[9px] uppercase tracking-widest font-sans font-bold text-sage bg-sage/10 px-1.5 py-0.5 rounded-full">
                            {t.modes.organizzativa.systemBadge || "Suggerita"}
                          </span>
                        )}
                        {!isSystem && (
                          <button
                            type="button"
                            data-testid={`${testidPrefix}-subtask-edit-${sub.id}`}
                            onClick={() => startEditingSubtask(sub.id, sub.title)}
                            className="text-ink-soft/70 hover:text-ink"
                          >
                            <PencilSimple size={12} weight="duotone" />
                          </button>
                        )}
                      </div>
                    )}
                    {sub.system_explanation && (
                      <p className="text-xs text-ink-soft leading-relaxed mt-1 italic">
                        {sub.system_explanation}
                      </p>
                    )}
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      <label className="text-[11px] text-ink-soft font-sans">{t.modes.organizzativa.subtaskDeadline}:</label>
                      {isAnonymous ? (
                        <button
                          type="button"
                          data-testid={`${testidPrefix}-subtask-deadline-${sub.id}`}
                          onClick={() => onAnonymousMutation?.(t.common.authReasonSetDeadline)}
                          className="inline-flex items-center gap-1.5 text-xs font-sans px-2.5 py-1 rounded-lg border border-border bg-surface text-ink-soft hover:border-terracotta/50 hover:text-ink transition-colors"
                        >
                          <CalendarBlank size={13} weight="duotone" className="shrink-0 text-terracotta" />
                          <span>{t.modes.organizzativa.addDeadline}</span>
                        </button>
                      ) : (
                        <DateField
                          size="sm"
                          value={sub.deadline}
                          onChange={(iso) => upsert({ subtask_id: sub.id, deadline: iso, title: isSystem ? undefined : sub.title })}
                          testid={`${testidPrefix}-subtask-deadline-${sub.id}`}
                        />
                      )}
                      {dueLabel && (
                        <span className="text-[10px] text-ink-soft/70 font-sans">{dueLabel}</span>
                      )}
                    </div>
                    <Input
                      data-testid={`${testidPrefix}-subtask-note-${sub.id}`}
                      defaultValue={sub.note || ""}
                      onBlur={(e) => {
                        const v = e.target.value.trim();
                        if ((sub.note || "") !== v) {
                          upsert({ subtask_id: sub.id, note: v, title: isSystem ? undefined : sub.title });
                        }
                      }}
                      placeholder={t.modes.organizzativa.subtaskNotePh}
                      className="h-8 text-xs rounded-lg mt-2"
                    />
                  </div>
                  {!isSystem && (
                    <button
                      type="button"
                      data-testid={`${testidPrefix}-subtask-delete-${sub.id}`}
                      onClick={() => removeSubtask(sub.id)}
                      aria-label={t.modes.organizzativa.subtaskDelete}
                      className="w-7 h-7 rounded-lg text-ink-soft/70 hover:text-terracotta hover:bg-terracotta/10 flex items-center justify-center shrink-0"
                    >
                      <Trash size={13} weight="duotone" />
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <div className="flex items-center gap-2">
        <Input
          data-testid={`${testidPrefix}-subtask-add-input-${parentItem.id}`}
          value={draftTitle}
          onChange={(e) => setDraftTitle(e.target.value)}
          onKeyDown={onKeyAdd}
          placeholder={t.modes.organizzativa.subtaskTitlePh}
          className="flex-1 h-9 text-sm rounded-lg"
        />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={addNew}
          disabled={!draftTitle.trim() || creating}
          data-testid={`${testidPrefix}-subtask-add-${parentItem.id}`}
          className="text-terracotta hover:bg-terracotta/10 rounded-full disabled:opacity-40"
        >
          <Plus size={13} weight="bold" className="mr-1" />
          {t.modes.organizzativa.subtaskAdd}
        </Button>
      </div>
    </div>
  );
}
